import { useMemo } from "react";
import { ModernInput } from "./ui-modern";
import { buildPresets, sameRange, stepRange, type DateRange } from "../lib/dateRange";

const STEP_BUTTON =
  "flex h-[46px] min-w-0 basis-[calc(50%_-_6px)] grow items-center justify-center gap-1.5 sm:order-none sm:w-10 sm:flex-none sm:basis-auto items-center justify-center rounded-[10px] border border-[#e2e7ee] bg-white text-lg text-[#071a33] hover:border-[#0f6d86] hover:text-[#0f6d86] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#e2e7ee] disabled:hover:text-[#071a33]";

/**
 * Report date filter: quick presets for the common cases, From/To date
 * inputs (native pickers — best on phones, keyboard-friendly on desktop)
 * for anything else, and ‹ › to step a day/month/year at a time. Picking a
 * single day is just From = To. Fully controlled; the parent owns the range.
 */
export function DateRangeFilter({ value, onChange }: { value: DateRange; onChange: (range: DateRange) => void }) {
  const presets = useMemo(() => buildPresets(), []);
  const active = presets.find((p) => sameRange(p.range, value));
  const prev = stepRange(value, -1);
  const next = stepRange(value, 1);

  function setFrom(from: string): void {
    // Keep From <= To: moving From past To drags To along with it.
    onChange({ from, to: from && value.to && from > value.to ? from : value.to });
  }
  function setTo(to: string): void {
    onChange({ from: to && value.from && to < value.from ? to : value.from, to });
  }

  return (
    <div className="mb-4 flex flex-col gap-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Quick date ranges">
        {presets.map((p) => {
          const on = active?.id === p.id;
          return (
            <button
              key={p.id}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(p.range)}
              className={`rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
                on
                  ? "border-[#071a33] bg-[#071a33] text-white"
                  : "border-[#e2e7ee] bg-white text-[#071a33] hover:border-[#0f6d86] hover:text-[#0f6d86]"
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-end gap-2 sm:gap-2.5">
        <button type="button" className={`${STEP_BUTTON} order-3 sm:order-none`} aria-label="Previous period" disabled={!prev} onClick={() => prev && onChange(prev)}>
          ‹<span className="text-[13px] font-semibold sm:hidden">Previous</span>
        </button>
        <label className="order-1 flex min-w-0 basis-[calc(50%_-_6px)] grow flex-col gap-1 text-[12px] font-semibold text-[#6b7a8f] sm:order-none sm:flex-none sm:basis-auto">
          From
          <ModernInput type="date" className="!w-full min-w-0 !px-2 text-[#071a33] sm:!w-auto sm:!px-3" value={value.from} max={value.to || undefined} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="order-2 flex min-w-0 basis-[calc(50%_-_6px)] grow flex-col gap-1 text-[12px] font-semibold text-[#6b7a8f] sm:order-none sm:flex-none sm:basis-auto">
          To
          <ModernInput type="date" className="!w-full min-w-0 !px-2 text-[#071a33] sm:!w-auto sm:!px-3" value={value.to} min={value.from || undefined} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button type="button" className={`${STEP_BUTTON} order-4 sm:order-none`} aria-label="Next period" disabled={!next} onClick={() => next && onChange(next)}>
          <span className="text-[13px] font-semibold sm:hidden">Next</span>›
        </button>
      </div>
    </div>
  );
}
